import { useState } from "react";
import { useTranslation } from "react-i18next";
import CIcon from "@coreui/icons-react";
import { cilCommentBubble, cilStar } from "@coreui/icons";
import {
  QUESTION_LIMIT,
  RATING_LIMIT,
  useFeedbackAllowance,
} from "../../../api/hooks/useTradingStrategyFeedback";
import { AskQuestionModal } from "./AskQuestionModal";
import { RateStrategyModal } from "./RateStrategyModal";
import { StrategyColumnLink } from "./StrategyColumnLink";
import type { TradingStrategyDto } from "../../../api/hooks/useTradingStrategies";

interface StrategyFeedbackLinksProps {
  strategy: TradingStrategyDto;
  /** Current or past — a cancelled subscription still earns a rating. */
  hasSubscribed: boolean;
}

export function StrategyFeedbackLinks({ strategy, hasSubscribed }: StrategyFeedbackLinksProps) {
  const { t } = useTranslation("tradingStrategies");
  const [asking, setAsking] = useState(false);
  const [rating, setRating] = useState(false);
  const [asked, setAsked] = useState(false);
  const [rated, setRated] = useState(false);

  // Mirrors the server's per-customer limit so the row can say no before a form is filled in.
  const questionAllowance = useFeedbackAllowance("questions", QUESTION_LIMIT);
  const ratingAllowance = useFeedbackAllowance("ratings", RATING_LIMIT);

  // Each modal thanks the customer on its way out, so its row goes rather than asking for something already given.
  const canAsk = !asked;
  const canRate = hasSubscribed && !rated;

  const askIcon = <CIcon icon={cilCommentBubble} />;
  const rateIcon = <CIcon icon={cilStar} />;

  return (
    <>
      {canAsk &&
        (questionAllowance.exhausted ? (
          <StrategyColumnLink icon={askIcon} muted>
            {t("feedback.links.askWaiting")}
          </StrategyColumnLink>
        ) : (
          <StrategyColumnLink icon={askIcon} onClick={() => setAsking(true)}>
            {t("feedback.links.ask")}
          </StrategyColumnLink>
        ))}

      {canRate &&
        (ratingAllowance.exhausted ? (
          <StrategyColumnLink icon={rateIcon} muted>
            {t("feedback.links.rateWaiting")}
          </StrategyColumnLink>
        ) : (
          <StrategyColumnLink icon={rateIcon} onClick={() => setRating(true)}>
            {t("feedback.links.rate")}
          </StrategyColumnLink>
        ))}

      {canAsk && (
        <AskQuestionModal
          strategy={strategy}
          visible={asking}
          onClose={() => setAsking(false)}
          onSent={questionAllowance.record}
          onRateLimited={questionAllowance.blockUntil}
          onAsked={() => {
            setAsking(false);
            setAsked(true);
          }}
        />
      )}

      {canRate && (
        <RateStrategyModal
          strategy={strategy}
          visible={rating}
          onClose={() => setRating(false)}
          onSent={ratingAllowance.record}
          onRateLimited={ratingAllowance.blockUntil}
          onRated={() => {
            setRating(false);
            setRated(true);
          }}
        />
      )}
    </>
  );
}
